import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { getSections, createSection, updateSection, deleteSection, getCategoriesPublic } from '../../services/api';
import { Tags, Plus, Edit, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorUtils';

const Secciones = () => {
  const { user } = useAuth();
  const [sections, setSections] = useState([]);
  const [globalCategories, setGlobalCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    order: 0,
    is_active: true,
    parent_category_id: '',
  });

  useEffect(() => {
    if (user?.business_id) loadSections();
    loadGlobalCategories();
  }, [user]);

  const loadGlobalCategories = async () => {
    try {
      const response = await getCategoriesPublic();
      setGlobalCategories(response.data);
    } catch (error) {
      console.error('Error loading global categories:', error);
    }
  };

  const loadSections = async () => {
    try {
      const response = await getSections({ business_id: user.business_id, active_only: false });
      setSections(response.data);
    } catch (error) {
      console.error('Error loading sections:', error);
      toast.error('Error al cargar secciones');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingItem) {
        await updateSection(editingItem.id, formData);
        toast.success('Sección actualizada');
      } else {
        await createSection({ ...formData, parent_category_id: formData.parent_category_id || null, business_id: user.business_id });
        toast.success('Sección creada');
      }
      setShowModal(false);
      resetForm();
      loadSections();
    } catch (error) {
      console.error('Error saving section:', error);
      toast.error(getErrorMessage(error, 'Error al guardar sección'));
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description || '',
      icon: item.icon || '',
      order: item.order || 0,
      is_active: item.is_active,
      parent_category_id: item.parent_category_id || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`¿Estás seguro de eliminar "${item.name}"?`)) return;

    try {
      await deleteSection(item.id);
      toast.success('Sección eliminada');
      loadSections();
    } catch (error) {
      console.error('Error deleting section:', error);
      toast.error(getErrorMessage(error, 'Error al eliminar sección'));
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', icon: '', order: 0, is_active: true, parent_category_id: '' });
    setEditingItem(null);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Secciones</h1>
          <p className="text-slate-500 mt-1">Organiza tus productos en secciones personalizadas</p>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center">
          <p className="text-slate-600">{sections.length} secciones</p>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nueva Sección
          </button>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {sections.map((item) => (
            <div
              key={item.id}
              className={`bg-white rounded-xl border ${
                item.is_active ? 'border-slate-200' : 'border-red-200 bg-red-50'
              } p-4 hover:shadow-md transition-shadow`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{item.icon || '📁'}</span>
                  <div>
                    <h3 className="font-semibold text-slate-900">{item.name}</h3>
                    {item.parent_category_name && (
                      <p className="text-xs text-teal-600 font-medium">{item.parent_category_name}</p>
                    )}
                    {item.description && (
                      <p className="text-sm text-slate-500 line-clamp-1">{item.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleEdit(item)}
                    className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(item)}
                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">Orden: {item.order}</span>
                  {item.source === 'comanda_pro' && (
                    <span className="px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-600">POS</span>
                  )}
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  item.is_active
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {item.is_active ? 'Activa' : 'Inactiva'}
                </span>
              </div>
            </div>
          ))}
        </div>

        {sections.length === 0 && (
          <div className="bg-white rounded-xl p-12 text-center border border-slate-200">
            <Tags className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">No hay secciones</h3>
            <p className="text-slate-500 mt-1">
              Crea secciones para organizar tus productos (ej: Bebidas, Tacos, Postres)
            </p>
            <button
              onClick={() => { resetForm(); setShowModal(true); }}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              Crear Primera Sección
            </button>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b border-slate-200">
                <h2 className="text-2xl font-bold text-slate-900">
                  {editingItem ? 'Editar Sección' : 'Nueva Sección'}
                </h2>
                <button
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Categoría Padre</label>
                  <select
                    value={formData.parent_category_id}
                    onChange={(e) => setFormData({ ...formData, parent_category_id: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">Sin categoría padre</option>
                    {globalCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Nombre *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ej: Bebidas, Tacos, Postres"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Descripción</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Ej: Refrescos, aguas, jugos"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Icono (emoji)</label>
                    <input
                      type="text"
                      value={formData.icon}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      placeholder="🥤"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Orden</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  />
                  <label htmlFor="is_active" className="text-sm text-slate-700">Sección activa</label>
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors"
                  >
                    {editingItem ? 'Actualizar' : 'Crear'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowModal(false); resetForm(); }}
                    className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Secciones;
